var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../layout/components/loaders');
var rowActionDropdown = require('../components/rowActionDropdown');
var flash = require('../components/flash');
var modal = require('../layout/components/modal');
var io = require('../libs/socketio');
var dragscroll = require('../libs/dragscroll');

var RECYCLE_BIN_UPDATE_EVENT = 'recycle-bin:update';
var TABLE_MIN_WIDTH = '62rem';

var state = {
  records: prop([]),
  total: prop(0),
  offset: prop(0),
  limit: prop(10),
  search: prop(''),
  searchActive: prop(false),
  loading: true,
  restoringRid: prop(null),
  migratingRid: prop(null),
  permanentDeletingRid: prop(null),
  migrateTargetRid: prop(''),
  pointDetails: prop({}),
  pointDetailsError: prop({}),
  pointDetailsLoadingRid: prop(null),
  message: prop([]),
  error: prop([]),
  socketHandler: null,
  searchTimer: null
};

function resetAlerts() {
  state.message([]);
  state.error([]);
}

function errorMessage(err, fallback) {
  if (err && err.error) return err.error;
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}

function showLoading() {
  state.loading = true;
  m.redraw();
}

function loadRecords(options) {
  options = options || {};
  showLoading();
  if (!options.keepAlerts) resetAlerts();
  return loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: '/api/v1/recycle-bin',
    data: {
      limit: state.limit(),
      offset: state.offset(),
      search: state.search()
    }
  })
  .then(function (result) {
    var records = Array.isArray(result) ? result : result.records;
    var total = Array.isArray(result) ? records.length : parseInt(result.total, 10);
    records = Array.isArray(records) ? records : [];
    total = Number.isFinite(total) && total >= 0 ? total : records.length;

    if (!records.length && total > 0 && state.offset() >= total) {
      state.offset(Math.max(0, Math.floor((total - 1) / state.limit()) * state.limit()));
      return loadRecords({ keepAlerts: true });
    }

    state.records(records);
    state.total(total);
  })
  .catch(function (err) {
    state.error([errorMessage(err, 'Failed to load Recycle Bin')]);
  })
  .finally(function () {
    state.loading = false;
  });
}

function restoreRecord(record) {
  if (!record || !record.rid) return;
  resetAlerts();
  state.restoringRid(record.rid);
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'PUT',
    url: '/api/v1/recycle-bin/' + record.rid.slice(1) + '/restore',
    body: { action: 'restore' }
  })
  .then(function () {
    return loadRecords({ keepAlerts: true });
  })
  .then(function () {
    state.message(['Restored ' + (record.name || record.class || record.rid)]);
  })
  .catch(function (err) {
    state.error([errorMessage(err, 'Failed to restore record')]);
  })
  .finally(function () {
    state.restoringRid(null);
  });
}

function recycleRecordPath(record, action) {
  var rid = record && record.rid ? String(record.rid).replace(/^#/, '') : '';
  return '/api/v1/recycle-bin/' + rid + '/' + action;
}

function setMappedValue(stream, key, value) {
  var next = Object.assign({}, stream());
  if (value === null || typeof value === 'undefined') {
    delete next[key];
  } else {
    next[key] = value;
  }
  stream(next);
}

function loadPointDetails(record) {
  if (!record || !record.rid) return Promise.resolve();
  var rid = record.rid;
  state.pointDetailsLoadingRid(rid);
  setMappedValue(state.pointDetailsError, rid, null);
  return loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: recycleRecordPath(record, 'point-details')
  })
  .then(function (result) {
    setMappedValue(state.pointDetails, rid, result || {});
  })
  .catch(function (err) {
    setMappedValue(state.pointDetailsError, rid, errorMessage(err, 'Failed to load point details'));
  })
  .finally(function () {
    if (state.pointDetailsLoadingRid() === rid) {
      state.pointDetailsLoadingRid(null);
    }
    m.redraw();
  });
}

function pointReasonLabel(record) {
  var reasons = String(record && record.pointRecycleReason || '')
    .split(',')
    .map(function (reason) { return reason.trim(); })
    .filter(Boolean);
  if (!reasons.length) return '';
  return reasons.join(', ');
}

function blockerLabel(blocker) {
  if (!blocker) return 'Unknown reference';
  if (blocker.edgeClass === 'Event.object') {
    return blocker.count + ' event records still target this point';
  }
  var target = blocker.otherName || blocker.otherClass || blocker.otherRid || 'record';
  return blocker.edgeClass + ' ' + blocker.direction + ' ' + target;
}

function detachedEdgeLabel(edge) {
  if (!edge) return 'Unknown detached link';
  var target = edge.otherRid || 'record';
  return edge.edgeClass + ' ' + edge.direction + ' ' + target;
}

function bucketStatusLabel(bucket) {
  if (!bucket) return '';
  if (bucket.status === 'samples') return bucket.bucket + ': samples found';
  if (bucket.status === 'error') return bucket.bucket + ': check failed';
  return bucket.bucket + ': no samples';
}

function influxStatusLabel(influxStatus) {
  if (!influxStatus) return 'InfluxDB status unavailable';
  if (influxStatus.checkFailed) return 'InfluxDB check failed';
  if (influxStatus.hasData) return 'InfluxDB samples found';
  return 'No InfluxDB samples found';
}

function renderDetailList(items, emptyLabel, labelFn) {
  items = Array.isArray(items) ? items : [];
  if (!items.length) return m('div.f5.gray', emptyLabel);
  return m('ul.mt1.mb0.pl3', items.slice(0, 8).map(function (item) {
    return m('li.f5.mb1', labelFn(item));
  }).concat(items.length > 8 ? [
    m('li.f5.gray', (items.length - 8) + ' more')
  ] : []));
}

function renderInfluxDetails(influxStatus) {
  var buckets = influxStatus && Array.isArray(influxStatus.buckets) ? influxStatus.buckets : [];
  var ids = influxStatus && Array.isArray(influxStatus.ids) ? influxStatus.ids : [];
  return m('div.mt3', [
    m('div.f5.b.mb1', 'Influx Data'),
    m('div.f5', influxStatusLabel(influxStatus)),
    ids.length ? m('div.f6.gray.mt1', 'IDs checked: ' + ids.join(', ')) : null,
    buckets.length ? m('ul.mt1.mb0.pl3', buckets.map(function (bucket) {
      return m('li.f6', bucketStatusLabel(bucket));
    })) : null
  ]);
}

function renderPointActionDetails(record) {
  var rid = record && record.rid;
  var details = rid ? state.pointDetails()[rid] : null;
  var error = rid ? state.pointDetailsError()[rid] : null;
  var loading = rid && state.pointDetailsLoadingRid() === rid && !details;

  return m('div.mt3', [
    loading ? m('div.f5.gray', 'Loading point details...') : null,
    error ? m('div.f5.red', error) : null,
    details ? [
      m('div.mt3', [
        m('div.f5.b.mb1', 'Current Point Connections'),
        renderDetailList(details.connections, 'No blocking point connections remain.', blockerLabel)
      ]),
      m('div.mt3', [
        m('div.f5.b.mb1', 'Detached Links'),
        renderDetailList(details.detachedEdges, 'No detached Source, table, or write links recorded.', detachedEdgeLabel)
      ]),
      renderInfluxDetails(details.influx)
    ] : null
  ]);
}

function errorMessagesWithBlockers(err, fallback) {
  var messages = [errorMessage(err, fallback)];
  if (err && Array.isArray(err.blockers) && err.blockers.length) {
    err.blockers.slice(0, 6).forEach(function (blocker) {
      messages.push(blockerLabel(blocker));
    });
    if (err.blockers.length > 6) {
      messages.push((err.blockers.length - 6) + ' more linked records');
    }
  }
  return messages;
}

function migratePointRecord(record) {
  var targetPointRid = state.migrateTargetRid().trim();
  if (!targetPointRid) {
    state.error(['Choose a target point RID.']);
    return Promise.resolve();
  }

  resetAlerts();
  state.migratingRid(record.rid);
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'POST',
    url: recycleRecordPath(record, 'migrate'),
    body: { targetPointRid: targetPointRid }
  })
  .then(function () {
    modal.vm.close();
    return loadRecords({ keepAlerts: true });
  })
  .then(function () {
    state.message(['Migrated data from ' + (record.name || record.rid) + ' to ' + targetPointRid]);
  })
  .catch(function (err) {
    state.error(errorMessagesWithBlockers(err, 'Failed to migrate point data'));
  })
  .finally(function () {
    state.migratingRid(null);
  });
}

function openMigratePointModal(record) {
  if (!record || record.class !== 'Point') return;
  resetAlerts();
  state.migrateTargetRid('');
  modal.vm.title('Migrate Point Data');
  modal.vm.body = function () {
    return m('div', [
      m('p.mv0', 'Move saved point events, aggregate variables, and samples from ' + (record.name || record.rid) + ' to another Point.'),
      pointReasonLabel(record) ? m('p.f5.gray', 'Retained because: ' + pointReasonLabel(record)) : null,
      record.migratedToRid ? m('p.f5.gray', 'Last migrated to ' + record.migratedToRid) : null,
      renderPointActionDetails(record),
      m('label.db.f5.mb1.mt3', 'Target Point RID'),
      m('input.form-control.w-100', {
        placeholder: '#12:34',
        value: state.migrateTargetRid(),
        oninput: function (ev) {
          state.migrateTargetRid(ev.target.value);
        }
      })
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Cancel',
      class: 'dib mr2',
      func: modal.vm.close
    },
    {
      label: state.migratingRid() === record.rid ? 'Migrating...' : 'Migrate',
      class: 'dib green',
      disabled: function () {
        return state.migratingRid() === record.rid;
      },
      func: function () {
        return migratePointRecord(record);
      }
    }
  ]);
  modal.vm.open();
  return loadPointDetails(record);
}

function permanentlyDeletePointRecord(record) {
  resetAlerts();
  state.permanentDeletingRid(record.rid);
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'DELETE',
    url: recycleRecordPath(record, 'permanent')
  })
  .then(function () {
    modal.vm.close();
    return loadRecords({ keepAlerts: true });
  })
  .then(function () {
    state.message(['Permanently deleted ' + (record.name || record.rid)]);
  })
  .catch(function (err) {
    state.error(errorMessagesWithBlockers(err, 'Failed to permanently delete point'));
  })
  .finally(function () {
    state.permanentDeletingRid(null);
  });
}

function openPermanentDeleteModal(record) {
  if (!record || record.class !== 'Point') return;
  resetAlerts();
  modal.vm.title('Delete Point Permanently?');
  modal.vm.body = function () {
    return m('div', [
      m('p.mv0', 'This removes ' + (record.name || record.rid) + ' after all linked records and samples have been cleared.'),
      pointReasonLabel(record) ? m('p.f5.gray', 'Retained because: ' + pointReasonLabel(record)) : null,
      renderPointActionDetails(record)
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Cancel',
      class: 'dib mr2',
      func: modal.vm.close
    },
    {
      label: state.permanentDeletingRid() === record.rid ? 'Deleting...' : 'Delete Permanently',
      class: 'dib red',
      disabled: function () {
        return state.permanentDeletingRid() === record.rid;
      },
      func: function () {
        return permanentlyDeletePointRecord(record);
      }
    }
  ]);
  modal.vm.open();
  return loadPointDetails(record);
}

function totalPages() {
  return Math.max(1, Math.ceil(state.total() / state.limit()));
}

function currentPage() {
  return Math.floor(state.offset() / state.limit()) + 1;
}

function setPage(page) {
  page = Math.max(1, Math.min(totalPages(), page));
  state.offset((page - 1) * state.limit());
  return loadRecords();
}

function scheduleSearch(value) {
  state.search(value);
  state.offset(0);
  showLoading();
  if (state.searchTimer) clearTimeout(state.searchTimer);
  state.searchTimer = setTimeout(function () {
    state.searchTimer = null;
    loadRecords();
  }, 250);
}

function clearSearch() {
  if (state.searchTimer) clearTimeout(state.searchTimer);
  state.searchTimer = null;
  state.search('');
  state.searchActive(false);
  state.offset(0);
  showLoading();
  return loadRecords();
}

function formatDeletedAt(value) {
  if (!value) return '';
  var date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function recordLabel(record) {
  var name = record.name || record.class || record.rid;
  if (record.roomNumber) {
    return record.roomNumber + ' ' + name;
  }
  return name;
}

function originalParentLabel(record) {
  return record.originalParentDisplay || record.originalParentRid || '';
}

function deletedByLabel(record) {
  return record.deletedByName || record.deletedByRid || '';
}

function resultRangeLabel() {
  if (!state.total()) return 'No records';
  var start = state.offset() + 1;
  var end = Math.min(state.offset() + state.records().length, state.total());
  return 'Showing ' + start + '-' + end + ' of ' + state.total();
}

function pageButton(label, disabled, action, extraClass) {
  return m('button.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.mr2.mb1.bg-transparent', {
    class: (disabled ? 'gray o-50' : 'blue') + (extraClass ? ' ' + extraClass : ''),
    disabled: disabled,
    onclick: disabled ? null : action
  }, label);
}

function renderPaginationControls() {
  if (state.total() <= state.limit()) return null;
  var page = currentPage();
  var pages = totalPages();
  return m('div.flex.flex-wrap.items-center.justify-between.pt2', [
    m('div.f5.gray.mb2', 'Page ' + page + ' of ' + pages),
    m('div.mb2', [
      pageButton('«', page <= 1, function () { return setPage(1); }),
      pageButton('<', page <= 1, function () { return setPage(page - 1); }),
      pageButton('>', page >= pages, function () { return setPage(page + 1); }),
      pageButton('» ' + pages, page >= pages, function () { return setPage(pages); }, 'mr0')
    ])
  ]);
}

function searchExpanded() {
  return state.searchActive() || !!state.search();
}

function renderSearchControls() {
  var expanded = searchExpanded();
  return m('div.f4.flex.flex-wrap.items-center.justify-between.mb3', [
    m('div.f4.flex.items-center.mb2', [
      m('span.ba.b--blue.br2.ph1.flex.items-center.mr2.h2', {
        id: 'paginationWithSearchSpan',
        class: expanded ? 'paginationWithSearchSpanBig' : 'paginationWithSearchSpanSmall',
        onclick: function (ev) {
          state.searchActive(true);
          setTimeout(function () {
            var wrapper = ev && ev.currentTarget ? ev.currentTarget : document.getElementById('paginationWithSearchSpan');
            var input = wrapper ? wrapper.querySelector('input') : null;
            if (input) input.focus();
          }, 1);
        }
      }, [
        m('div.nowrap', { style: { display: expanded ? 'none' : 'block' } }, [
          m('i', {
            id: 'equipment-paginated-table-search-icon',
            class: 'blue f2 mh3 ion-search pointer'
          })
        ]),
        m('div.flex.items-center', { style: { display: expanded ? 'flex' : 'none' } }, [
          m('input.mh1.mv1.w4', {
            id: 'paginated-table-search-input',
            placeholder: 'Search Recycle Bin',
            value: state.search(),
            autocomplete: 'off',
            autocorrect: 'off',
            spellcheck: 'false',
            oninput: function (ev) {
              scheduleSearch(ev.target.value);
            },
            onblur: function () {
              if (!state.search()) {
                state.searchActive(false);
              }
            },
            onfocusin: function () {
              state.searchActive(true);
            }
          }),
          state.search() ?
            m('i', {
              class: 'blue f4 mr1 ion-close-circled pointer',
              onclick: function (ev) {
                if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
                return clearSearch();
              }
            }) :
            null
        ])
      ])
    ]),
    m('div.f5.gray.mb2', resultRangeLabel())
  ]);
}

function skeletonBar(width) {
  return m('div.f4.br2.bg-dark-gray.o-20', {
    style: { height: '1em', width: width + '%' }
  });
}

function skeletonCell(width, extraClass) {
  return m('td.pv2.pr3.nowrap.skeleton' + (extraClass ? '.' + extraClass : ''), [
    skeletonBar(width)
  ]);
}

function renderSkeletonRows() {
  var rows = Math.max(1, state.limit());
  var widths = [70, 40, 65, 55, 70, 24];
  var bodyRows = [];
  for (var index = 0; index < rows; index += 1) {
    bodyRows.push(m('tr.bb.b--moon-gray', [
      skeletonCell(widths[(index + 0) % widths.length]),
      skeletonCell(widths[(index + 1) % widths.length]),
      skeletonCell(widths[(index + 2) % widths.length]),
      skeletonCell(widths[(index + 3) % widths.length]),
      skeletonCell(widths[(index + 4) % widths.length]),
      skeletonCell(widths[(index + 5) % widths.length], 'tc')
    ]));
  }
  return bodyRows;
}

function renderRestoreMenu(record) {
  var restoring = state.restoringRid() === record.rid;
  return m(rowActionDropdown, {
    items: [
      {
        label: restoring ? 'Restoring...' : 'Restore',
        icon: 'ion-ios-undo',
        class: restoring ? 'gray' : 'green',
        onclick: function () {
          if (restoring) return;
          return restoreRecord(record);
        }
      }
    ]
  });
}

function renderPointMenu(record) {
  var migrating = state.migratingRid() === record.rid;
  var deleting = state.permanentDeletingRid() === record.rid;
  return m(rowActionDropdown, {
    items: [
      {
        label: migrating ? 'Migrating...' : 'Migrate Data',
        icon: 'ion-arrow-swap',
        class: migrating ? 'gray' : 'blue',
        onclick: function () {
          if (migrating) return;
          return openMigratePointModal(record);
        }
      },
      {
        label: deleting ? 'Deleting...' : 'Delete Permanently',
        icon: 'ion-ios-trash',
        class: deleting ? 'gray' : 'red',
        onclick: function () {
          if (deleting) return;
          return openPermanentDeleteModal(record);
        }
      }
    ]
  });
}

function renderRecordType(record) {
  if (!record || record.class !== 'Point') return record.class || '';
  var reason = pointReasonLabel(record);
  return m('span', [
    'Point',
    reason ? m('span.f6.gray', ' (' + reason + ')') : null
  ]);
}

function renderTable(records) {
  return m('div.overflow-hidden.mb3', [
    m('div.dragscroll-container', [
      m('div.dragscroll.pb3.pr4', { oncreate: dragscroll, onupdate: dragscroll }, [
        m('table.w-100.collapse', { style: { minWidth: TABLE_MIN_WIDTH } }, [
          m('thead', [
            m('tr.bb.b--mid-gray', [
              m('th.tl.pv2.pr3.nowrap', 'Name'),
              m('th.tl.pv2.pr3.nowrap', 'Type'),
              m('th.tl.pv2.pr3.nowrap', 'Deleted'),
              m('th.tl.pv2.pr3.nowrap', 'Deleted By'),
              m('th.tl.pv2.pr3.nowrap', 'Original Parent'),
              m('th.tc.pv2.pr3.nowrap', '')
            ])
          ]),
          m('tbody', state.loading ? renderSkeletonRows() : records.map(function (record) {
            return m('tr.bb.b--moon-gray', {
              key: record.rid,
              'data-recycle-rid': record.rid
            }, [
              m('td.pv2.pr3.nowrap', recordLabel(record)),
              m('td.pv2.pr3.nowrap', renderRecordType(record)),
              m('td.pv2.pr3.nowrap', formatDeletedAt(record.deletedAt)),
              m('td.pv2.pr3.nowrap', deletedByLabel(record)),
              m('td.pv2.pr3.nowrap', originalParentLabel(record)),
              m('td.pv2.pr3.tc.nowrap', [
                record.class === 'Point' ? renderPointMenu(record) : renderRestoreMenu(record)
              ])
            ]);
          }))
        ])
      ])
    ])
  ]);
}

var recycleBin = {
  oninit: function () {
    if (state.socketHandler) {
      io.off(RECYCLE_BIN_UPDATE_EVENT, state.socketHandler);
    }
    state.socketHandler = function () {
      if (state.restoringRid()) return;
      return loadRecords();
    };
    io.on(RECYCLE_BIN_UPDATE_EVENT, state.socketHandler);
    return loadRecords();
  },
  onremove: function () {
    if (state.searchTimer) {
      clearTimeout(state.searchTimer);
      state.searchTimer = null;
    }
    if (state.socketHandler) {
      io.off(RECYCLE_BIN_UPDATE_EVENT, state.socketHandler);
      state.socketHandler = null;
    }
  },
  view: function () {
    var records = state.records();
    return m('div', [
      m('div.flex.justify-between.items-center.mb3', [
        m('h3.mt0.mb0', 'Recycle Bin')
      ]),
      renderSearchControls(),
      state.error().length ? m(flash, { items: state.error(), bg: 'bg-red' }) : null,
      state.message().length ? m(flash, { items: state.message(), bg: 'bg-green' }) : null,
      state.loading || records.length ?
        renderTable(records) :
        m('div.gray', state.search() ? 'No Recycle Bin records match your search.' : 'Recycle Bin is empty.'),
      renderPaginationControls()
    ]);
  }
};

module.exports = recycleBin;
